import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ChevronLeft, MapPin, Award, Calendar } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { DEFAULT_EVENT_IMAGE, handleEventImageError } from '../utils/imageUrl';
import { Badge } from '../components/ui/badge';
import { Footer } from '../components/Footer';
import LevelAvatar from '../components/engagement/LevelAvatar';
import { fetchPublicProfile } from '../lib/meApi';
import { SEO } from '../components/SEO';

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('es-CL', { day: 'numeric', month: 'short' });
  } catch { return ''; }
};

const PublicProfilePage = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPublicProfile(username)
      .then(setProfile)
      .catch(() => setError('Perfil no encontrado'));
  }, [username]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-b from-emerald-50 to-white">
        <div className="text-center">
          <p className="text-6xl mb-3">🔍</p>
          <h1 className="text-2xl font-bold text-[#064E3B] mb-2">Perfil no encontrado</h1>
          <p className="text-gray-500 mb-6">El usuario <code className="bg-gray-100 px-1.5 py-0.5 rounded">@{username}</code> no existe o aún no tiene perfil público.</p>
          <Button onClick={() => navigate('/')} className="rounded-full bg-[#00CC66] hover:bg-[#00B359]" data-testid="profile-back-home">
            Volver al inicio
          </Button>
        </div>
      </div>
    );
  }

  if (!profile) {
    return <div className="min-h-screen flex items-center justify-center text-gray-400">Cargando…</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white">
      <SEO
        title={`${profile.name} en PlanCity (@${profile.username})`}
        description={profile.bio || `Mira los planes favoritos de ${profile.name} en PlanCity`}
      />
      <div className="max-w-3xl mx-auto px-4 py-6 md:py-10">
        <Button variant="ghost" size="sm" className="rounded-full -ml-2 mb-4" onClick={() => navigate(-1)}>
          <ChevronLeft className="w-5 h-5" /> Volver
        </Button>

        {/* Hero */}
        <Card className="rounded-3xl border-0 shadow-md overflow-hidden">
          <div className="h-28 bg-gradient-to-br from-emerald-500 via-emerald-600 to-[#064E3B]" />
          <CardContent className="px-5 md:px-8 pb-6 -mt-12">
            <div className="flex items-end gap-4">
              <div className="ring-4 ring-white rounded-full">
                <LevelAvatar
                  name={profile.name}
                  color={profile.avatar_color}
                  avatarUrl={profile.avatar_url}
                  level={profile.level}
                  size="xl"
                />
              </div>
              <div className="flex-1 min-w-0 pb-1">
                <h1 className="text-2xl font-bold text-[#064E3B] truncate" data-testid="public-profile-name">{profile.name}</h1>
                <p className="text-sm text-gray-500">@{profile.username}</p>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Badge className="bg-emerald-100 text-emerald-700 border-0 rounded-full">
                Nivel {profile.level} · {profile.level_name}
              </Badge>
              <Badge className="bg-amber-100 text-amber-700 border-0 rounded-full">
                {profile.points} PlanPoints
              </Badge>
              <Badge className="bg-violet-100 text-violet-700 border-0 rounded-full">
                {profile.badges.length} logros
              </Badge>
            </div>
            {profile.bio && (
              <p className="mt-4 text-gray-700 text-sm leading-relaxed">{profile.bio}</p>
            )}
          </CardContent>
        </Card>

        {/* Badges */}
        {profile.badges.length > 0 && (
          <div className="mt-8">
            <h2 className="font-bold text-[#064E3B] text-lg mb-3 flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-500" /> Logros desbloqueados
            </h2>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3" data-testid="public-profile-badges">
              {profile.badges.map(b => (
                <div key={b.code} className="bg-white rounded-2xl p-3 shadow-sm hover:shadow-md transition-shadow text-center">
                  <div className="text-3xl mb-1">{b.emoji}</div>
                  <p className="text-xs font-semibold text-[#064E3B] truncate">{b.name}</p>
                  <p className="text-[10px] text-gray-400">{fmtDate(b.earned_at)}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Public lists */}
        {profile.public_lists.length > 0 && (
          <div className="mt-8">
            <h2 className="font-bold text-[#064E3B] text-lg mb-3">Listas públicas</h2>
            <div className="space-y-4" data-testid="public-profile-lists">
              {profile.public_lists.map(lst => (
                <Card key={lst.id} className="rounded-2xl border-0 shadow-sm">
                  <CardContent className="p-5">
                    <div className="flex items-start gap-3 mb-3">
                      <span className="text-3xl">{lst.emoji}</span>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-[#064E3B] truncate">{lst.name}</h3>
                        {lst.description && <p className="text-xs text-gray-500 line-clamp-2">{lst.description}</p>}
                        <p className="text-[11px] text-emerald-600 mt-1">{lst.item_count} {lst.item_count === 1 ? 'evento' : 'eventos'}</p>
                      </div>
                    </div>
                    {lst.items_preview.length > 0 && (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {lst.items_preview.map(ev => (
                          <Link
                            key={ev.id}
                            to={`/event/${ev.id}`}
                            className="block rounded-xl overflow-hidden bg-gray-50 hover:shadow-md transition-shadow group"
                          >
                            {ev.main_image ? (
                              <div className="aspect-[3/4] overflow-hidden">
                                <img src={ev.main_image} alt={ev.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" onError={handleEventImageError} />
                              </div>
                            ) : (
                              <div className="aspect-[3/4] overflow-hidden">
                                <img src={DEFAULT_EVENT_IMAGE} alt={ev.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                              </div>
                            )}
                            <div className="p-2">
                              <p className="text-xs font-semibold text-[#064E3B] truncate">{ev.name}</p>
                              {ev.commune_name && (
                                <p className="text-[10px] text-gray-400 truncate flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5" />{ev.commune_name}</p>
                              )}
                            </div>
                          </Link>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {profile.badges.length === 0 && profile.public_lists.length === 0 && (
          <p className="text-center py-12 text-gray-400">Este perfil aún no tiene contenido público.</p>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default PublicProfilePage;
